import React, { useEffect, useRef, useState } from "react";
import { Skeleton, Typography } from "antd";

const TYPING_SPEED_MS = 18;

function useTypingEffect(text: string | null, active: boolean): string {
  const [displayed, setDisplayed] = useState(text ?? "");
  const indexRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!active || !text) {
      setDisplayed(text ?? "");
      return;
    }
    setDisplayed("");
    indexRef.current = 0;
    const tick = () => {
      indexRef.current += 1;
      setDisplayed(text.slice(0, indexRef.current));
      if (indexRef.current < text.length) {
        timerRef.current = setTimeout(tick, TYPING_SPEED_MS);
      }
    };
    timerRef.current = setTimeout(tick, TYPING_SPEED_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [text, active]);

  return displayed;
}

interface SummaryBlockProps {
  isPending: boolean;
  isPartial: boolean;
  isPostAssessment: boolean;
  overallReasoning: string | null;
  executiveSummary: string | null;
  fitCategory: "Matches" | "Partial Match" | "Not a Match" | null;
  aiJustArrived: boolean;
}

const SummaryBlock: React.FC<SummaryBlockProps> = ({
  isPending,
  isPartial,
  isPostAssessment,
  overallReasoning,
  executiveSummary,
  fitCategory,
  aiJustArrived,
}) => {
  const text = isPostAssessment && overallReasoning ? overallReasoning : executiveSummary;
  const displayed = useTypingEffect(text ?? null, aiJustArrived);

  if (isPending || isPartial) {
    return <Skeleton active paragraph={{ rows: 3 }} title={false} />;
  }

  const summaryClass =
    fitCategory === "Matches"
      ? "aic-summary-success"
      : fitCategory === "Partial Match"
        ? "aic-summary-warning"
        : "aic-summary-danger";

  return (
    <div className={`aic-summary ${summaryClass}`}>
      <Typography.Paragraph
        type="secondary"
        style={{ fontStyle: "italic", margin: 0 }}
      >
        "{aiJustArrived ? displayed : text}"
      </Typography.Paragraph>
    </div>
  );
};

export default SummaryBlock;
